const CHINESE_TEXT_REG = /^[\u4E00-\u9FFF]+$/;
const LETTER_TEXT_REG = /^\w+$/;

function flatCityList (list) {
  return list.reduce((cities, group) => cities.concat(group[1]), []);
}

function normalizeText (text) {
  return String(text || '').trim().toLowerCase();
}

function stripAdministrativeSuffix (text) {
  return String(text || '').replace(/(自治州|地区|盟|市|县|区)$/g, '');
}

function isOrderedLetters (text, keyword) {
  if (keyword.length < 2) {
    return false;
  }

  let keywordIndex = 0;

  for (let i = 0; i < text.length && keywordIndex < keyword.length; i++) {
    if (text.charAt(i) === keyword.charAt(keywordIndex)) {
      keywordIndex++;
    }
  }

  return keywordIndex === keyword.length;
}

function getTextMatchScore (value, keyword, exactScore, prefixScore, includeScore) {
  if (!value) {
    return -1;
  }

  const normalizedValue = String(value);
  const normalizedKeyword = String(keyword);
  const strippedValue = stripAdministrativeSuffix(normalizedValue);
  const strippedKeyword = stripAdministrativeSuffix(normalizedKeyword);

  if (normalizedValue === normalizedKeyword || strippedValue === strippedKeyword) {
    return exactScore;
  }

  if (normalizedValue.indexOf(normalizedKeyword) === 0 || strippedValue.indexOf(strippedKeyword) === 0) {
    return prefixScore;
  }

  if (normalizedValue.indexOf(normalizedKeyword) > -1 || strippedValue.indexOf(strippedKeyword) > -1) {
    return includeScore;
  }

  return -1;
}

function getLetterMatchScore (value, keyword, exactScore, prefixScore, orderedScore) {
  if (!value) {
    return -1;
  }

  const normalizedValue = normalizeText(value);

  if (normalizedValue === keyword) {
    return exactScore;
  }

  if (normalizedValue.indexOf(keyword) === 0) {
    return prefixScore;
  }

  if (isOrderedLetters(normalizedValue, keyword)) {
    return orderedScore;
  }

  return -1;
}

function setBestScore (currentScore, nextScore) {
  if (nextScore < 0) {
    return currentScore;
  }

  if (currentScore < 0 || nextScore < currentScore) {
    return nextScore;
  }

  return currentScore;
}

function getChineseMatchScore (city, keyword) {
  const hasDistrict = !!city.districtName;
  let score = -1;

  if (!hasDistrict) {
    score = setBestScore(score, getTextMatchScore(city.cityName || city.name, keyword, 0, 3, 7));
  } else {
    score = setBestScore(score, getTextMatchScore(city.districtName, keyword, 1, 2, 6));
    score = setBestScore(score, getTextMatchScore(city.cityName, keyword, 4, 5, 8));
  }

  score = setBestScore(score, getTextMatchScore(city.name, keyword, hasDistrict ? 1 : 0, hasDistrict ? 2 : 3, hasDistrict ? 6 : 7));
  return score;
}

function getPinyinMatchScore (city, keyword) {
  const hasDistrict = !!city.districtName;
  const districtPrefixScore = keyword.length > 1 ? 2 : 4;
  let score = -1;

  if (!hasDistrict) {
    score = setBestScore(score, getLetterMatchScore(city.cityPinyin || city.pinyin, keyword, 0, 3, 7));
    score = setBestScore(score, getLetterMatchScore(city.cityAcronym || city.acronym, keyword, 0, 3, 7));
  } else {
    score = setBestScore(score, getLetterMatchScore(city.districtPinyin, keyword, 1, districtPrefixScore, 2));
    score = setBestScore(score, getLetterMatchScore(city.districtAcronym, keyword, 1, districtPrefixScore, 2));
    score = setBestScore(score, getLetterMatchScore(city.cityPinyin || city.pinyin, keyword, 4, 5, 8));
    score = setBestScore(score, getLetterMatchScore(city.cityAcronym || city.acronym, keyword, 4, 5, 8));
  }

  return score;
}

export const getSearchList = (text, list, canSearchSpell) => {
  const keyword = normalizeText(text);

  if (!keyword) {
    return [];
  }

  const canSearchChinese = CHINESE_TEXT_REG.test(keyword);
  const canSearchLetters = LETTER_TEXT_REG.test(keyword) && canSearchSpell;

  if (!canSearchChinese && !canSearchLetters) {
    return [];
  }

  return flatCityList(list)
    .map((city, index) => {
      const score = canSearchLetters
        ? getPinyinMatchScore(city, keyword)
        : getChineseMatchScore(city, keyword);

      return {
        city,
        index,
        score
      };
    })
    .filter(item => item.score > -1)
    .sort((a, b) => {
      if (a.score !== b.score) {
        return a.score - b.score;
      }
      return a.index - b.index;
    })
    .map(item => item.city);
};

export function splitTextByKeyword (text, keyword) {
  const source = String(text || '');
  const normalizedKeyword = String(keyword || '').trim();

  if (!source || !normalizedKeyword) {
    return [{text: source, matched: false}];
  }

  const lowerSource = source.toLocaleLowerCase();
  const lowerKeyword = normalizedKeyword.toLocaleLowerCase();
  const parts = [];
  let start = 0;
  let index = lowerSource.indexOf(lowerKeyword, start);

  while (index !== -1) {
    if (index > start) {
      parts.push({text: source.slice(start, index), matched: false});
    }
    parts.push({
      text: source.slice(index, index + normalizedKeyword.length),
      matched: true
    });
    start = index + normalizedKeyword.length;
    index = lowerSource.indexOf(lowerKeyword, start);
  }

  if (start < source.length) {
    parts.push({text: source.slice(start), matched: false});
  }

  return parts.length ? parts : [{text: source, matched: false}];
}
